import { useCurrentUserContext } from '@/modules/auth/hooks/useCurrentUserContext';
import { ApplicationService } from '@/services/application';
import {
  ApplicationStage,
  IApplicationWithOrderAndCourse,
} from '@/types/application';
import { UserRole } from '@/types/user';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import debounce from 'lodash.debounce';
import { MRT_ColumnFiltersState, MRT_TableOptions } from 'material-react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetApplications } from '../hooks/useApplications';
import { ApplicationsSearchParams } from '../types';
import { ListApplicationsTable } from './ListApplicationsTable/ListApplicationsTable';

const routeApi = getRouteApi('/_admin/applications/');

export function ApplicationsTab() {
  const {
    currentUser,
    taxBenefitsProvider,
    educationProvider,
    isLoading: isLoadingUser,
  } = useCurrentUserContext() ?? {};
  const search = routeApi.useSearch() as ApplicationsSearchParams;
  const navigate = routeApi.useNavigate();
  const queryClient = useQueryClient();
  const [countsByStage, setCountsByStage] = useState<
    Record<ApplicationStage, number> | undefined
  >();

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([
    {
      id: 'applicationStage',
      value: search.applicationStage || ApplicationStage.Applying,
    },
  ]);
  const [globalFilter, setGlobalFilter] = useState<string>(search.term ?? '');
  const applicationStageFilter = columnFilters.find(
    columnFilter => columnFilter.id === 'applicationStage',
  );
  const isLoadingApplicationCounts =
    queryClient.isFetching({
      predicate: query =>
        query.queryKey.includes(ApplicationService.PATH) &&
        Object.values(ApplicationStage).some(stage =>
          query.queryKey.includes(stage),
        ),
    }) > 0;

  const filters = useMemo(
    () => ({
      ...(currentUser?.roles.includes(UserRole.TaxBenefitsProvider) &&
        taxBenefitsProvider?._id && {
          taxBenefitsProviderId: taxBenefitsProvider._id,
          flexibleRemuneration: true,
        }),
      ...(currentUser?.roles.includes(UserRole.EducationProvider) &&
        educationProvider && {
          educationProviderId: educationProvider._id,
        }),
      ...(search.companyId && { companyId: search.companyId }),
      ...(search.applicationTerm && search.applicationTerm === 'LEARNING_BUDGET'
        ? {
            hasLearningBudget: true,
          }
        : {
            applicationTerm: search.applicationTerm,
          }),
      ...(search.term && { term: search.term }),
      ...(search.applicationStage && {
        applicationStage: search.applicationStage,
      }),
    }),
    [currentUser, taxBenefitsProvider, educationProvider, search],
  );

  const {
    isLoading: isLoadingApplications,
    isRefetching: isRefetchingApplications,
    data: {
      data: applications = [],
      metadata: { total = 0, page = 1 } = {},
    } = {},
  } = useGetApplications(
    filters,
    {
      page: search.page ?? 1,
      limit: 10,
    },
    ['order'],
    {
      enabled: !isLoadingUser && !!filters.applicationStage,
      placeholderData: keepPreviousData,
    },
  );

  const debouncedSearchTerm = useMemo(() => {
    return debounce(term => {
      navigate({
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          term,
          page: 1,
        }),
      });
    }, 300);
  }, [navigate]);

  const updateStageCounts = useCallback(async () => {
    const counts = (
      await Promise.all(
        Object.values(ApplicationStage).map(stage => {
          return queryClient
            .fetchQuery({
              queryKey: [
                ApplicationService.PATH,
                { applicationStage: stage },
                { limit: 10, page: 1 },
                { tag: 'counts' },
                ['order'],
              ],
              queryFn: () =>
                ApplicationService.getApplications(
                  { applicationStage: stage as ApplicationStage },
                  {
                    page: 1,
                  },
                ),
            })
            .then(data => {
              return {
                stage,
                count: data.metadata?.total ?? 0,
              };
            });
        }),
      )
    ).reduce(
      (acc, { stage, count }) => ({
        ...acc,
        [stage]: count,
      }),
      {} as Record<ApplicationStage, number>,
    );
    setCountsByStage(counts);
  }, [queryClient]);

  const tableOptions: Omit<
    MRT_TableOptions<IApplicationWithOrderAndCourse>,
    'columns'
  > = {
    data: applications as IApplicationWithOrderAndCourse[],
    state: {
      isLoading:
        isLoadingUser ||
        isLoadingApplications ||
        isRefetchingApplications ||
        isLoadingApplicationCounts,
      pagination: {
        pageIndex: (page ?? 1) - 1 || 0,
        pageSize: 10,
      },
      columnPinning: {
        right: ['actions'],
        left: ['mrt-row-select', 'user'],
      },
      columnVisibility: {
        applicationStage: false,
        actions: !!(
          applicationStageFilter?.value === ApplicationStage.ReviewRequired ||
          (currentUser?.roles.includes(UserRole.Employer) &&
            applicationStageFilter?.value === ApplicationStage.InProgress) ||
          (currentUser?.roles.includes(UserRole.EducationProvider) &&
            applicationStageFilter?.value === ApplicationStage.Completed)
        ),
      },
      columnFilters: columnFilters,
      globalFilter,
    },
    initialState: {
      columnFilters: [
        {
          id: 'applicationStage',
          value: search.applicationStage,
        },
      ],
      globalFilter,
    },
    rowCount: total,
    onPaginationChange: updater => {
      const newState =
        typeof updater === 'function'
          ? updater({
              pageIndex: (page ?? 1) - 1 || 0,
              pageSize: 10,
            })
          : updater;

      navigate({
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          page: newState.pageIndex + 1,
        }),
      });
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: (value: string | undefined) => {
      setGlobalFilter(value ?? '');
      debouncedSearchTerm(value);
    },
    manualPagination: true,
    manualFiltering: true,
  };

  useEffect(() => {
    return () => {
      debouncedSearchTerm.cancel();
    };
  }, [debouncedSearchTerm]);

  useEffect(() => {
    navigate({
      search: columnFilters
        .map(({ id, value }) => [id, value])
        .reduce(
          (acc, [id, value]) => {
            const hasMeaningfulValue = Array.isArray(value)
              ? value.length > 0
              : !!value;

            if (hasMeaningfulValue) {
              acc[id as string] = value;
            }

            return acc;
          },
          {} as Record<string, unknown>,
        ),
    });
  }, [columnFilters, navigate]);

  useEffect(() => {
    if (queryClient) {
      updateStageCounts();
    }
  }, [queryClient, updateStageCounts]);

  return (
    <ListApplicationsTable
      tableOptions={tableOptions}
      countsByStage={countsByStage}
      onReviewApplications={async () => {
        await queryClient.invalidateQueries({
          queryKey: [ApplicationService.PATH],
          refetchType: 'all',
        });
        await updateStageCounts();
      }}
    />
  );
}
